import styled, { css } from 'styled-components';
import { Container } from '../Grid/Container';
import { Typography } from '../Typography';
import { Link } from 'react-router-dom';
import { useGTM } from '../../hooks/useGTM';
import {
  FaLinkedin,
  FaGithub,
  FaInstagram,
  FaMapMarkerAlt,
  FaEnvelope,
  FaPhone
} from 'react-icons/fa';

const FooterWrapper = styled.footer`
  ${({ theme }) => css`
    background: ${theme.colors.background.darker};
    padding: ${theme.spacing['2xl']} 0;
    position: relative;
    overflow: hidden;

    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: ${theme.effects.circuitPattern};
      opacity: 0.05;
      pointer-events: none;
    }
  `}
`;

const FooterGrid = styled.div`
  ${({ theme }) => css`
    display: grid;
    grid-template-columns: 2fr 1fr 1fr 1fr;
    gap: ${theme.spacing.xl};
    position: relative;
    z-index: 1;

    @media (max-width: 768px) {
      grid-template-columns: 1fr;
      gap: ${theme.spacing.lg};
      text-align: center;
    }
  `}
`;

const FooterLogo = styled.img`
  ${({ theme }) => css`
    height: 40px;
    width: auto;
    margin-bottom: ${theme.spacing.md};
  `}
`;

const FooterColumn = styled.section`
  ${({ theme }) => css`
    h3 {
      color: ${theme.colors.primary.main};
      margin-bottom: ${theme.spacing.md};
    }
  `}
`;

const FooterNav = styled.nav`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing.sm};
  `}
`;

const FooterLink = styled(Link)`
  ${({ theme }) => css`
    color: ${theme.colors.text.secondary};
    text-decoration: none;
    transition: color 0.2s ease;

    &:hover {
      color: ${theme.colors.primary.main};
    }
  `}
`;

const SocialLinks = styled.nav`
  ${({ theme }) => css`
    display: flex;
    gap: ${theme.spacing.md};
    margin-top: ${theme.spacing.md};

    @media (max-width: 768px) {
      justify-content: center;
    }
  `}
`;

const SocialIcon = styled.a`
  ${({ theme }) => css`
    color: ${theme.colors.text.secondary};
    font-size: 1.5rem;
    transition: all 0.2s ease;

    &:hover {
      color: ${theme.colors.primary.main};
      transform: translateY(-2px);
    }
  `}
`;

const ContactInfo = styled.address`
  ${({ theme }) => css`
    font-style: normal;
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing.sm};

    p {
      display: flex;
      align-items: center;
      gap: ${theme.spacing.sm};
      color: ${theme.colors.text.secondary};
    }

    svg {
      color: ${theme.colors.primary.main};
    }

    a {
      color: ${theme.colors.text.secondary};
      text-decoration: none;
      transition: color 0.2s;

      &:hover {
        color: ${theme.colors.primary.main};
      }
    }
  `}
`;

const CNPJText = styled(Typography)`
  ${({ theme }) => css`
    margin-top: ${theme.spacing.md};
  `}
`;

const Copyright = styled.div`
  ${({ theme }) => css`
    text-align: center;
    margin-top: ${theme.spacing.xl};
    padding-top: ${theme.spacing.xl};
    border-top: 1px solid ${theme.colors.primary.main}20;
    color: ${theme.colors.text.secondary};
  `}
`;

export const Footer = () => {
  const { pushEvent } = useGTM();

  const handleLinkClick = (linkName: string) => {
    pushEvent('footer_link_click', {
      link_name: linkName,
      section: 'footer'
    });
  };

  const handleSocialClick = (platform: string) => {
    pushEvent('social_link_click', {
      platform,
      section: 'footer'
    });
  };

  return (
    <FooterWrapper role="contentinfo">
      <Container>
        <FooterGrid>
          <FooterColumn as="nav">
            <FooterLogo src="/fagundes.tech-white.svg" alt="Logo Fagundes Tech" width="150" height="40" />
            <Typography $variant="body2" $color="secondary">
              Transformando ideias em realidade digital através de soluções 
              tecnológicas inovadoras e personalizadas.
                <CNPJText $variant="body2" $color="secondary">
                  CNPJ: 59.255.619/0001-90
                </CNPJText>
            </Typography>
            <SocialLinks role="navigation" aria-label="Links de redes sociais">
              <SocialIcon 
                href="https://linkedin.com/in/fagundestech" 
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => handleSocialClick('linkedin')}
              >
                <FaLinkedin />
              </SocialIcon>
              <SocialIcon 
                href="https://instagram.com/fagundestech" 
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => handleSocialClick('instagram')}
              >
                <FaInstagram />
              </SocialIcon>
            </SocialLinks>
          </FooterColumn>

          <FooterColumn as="nav">
            <Typography $variant="h3">Navegação</Typography>
            <FooterNav aria-label="Links de navegação do rodapé">
              <FooterLink to="/" onClick={() => handleLinkClick('home')}>
                Home
              </FooterLink>
              <FooterLink to="/about" onClick={() => handleLinkClick('about')}>
                Sobre
              </FooterLink>
              <FooterLink to="/portfolio" onClick={() => handleLinkClick('portfolio')}>
                Portfolio
              </FooterLink>
              <FooterLink to="/contact" onClick={() => handleLinkClick('contact')}>
                Contato
              </FooterLink>
            </FooterNav>
          </FooterColumn>

          <FooterColumn as="nav">
            <Typography $variant="h3">Serviços</Typography>
            <FooterNav aria-label="Links de serviços">
              <FooterLink to="/services#web" onClick={() => handleLinkClick('web-dev')}>
                Desenvolvimento Web
              </FooterLink>
              <FooterLink to="/services#mobile" onClick={() => handleLinkClick('mobile-dev')}>
                Apps Mobile
              </FooterLink>
              <FooterLink to="/services#cloud" onClick={() => handleLinkClick('cloud')}>
                Cloud Solutions
              </FooterLink>
              <FooterLink to="/services#data" onClick={() => handleLinkClick('data')}>
                Data Analytics
              </FooterLink>
            </FooterNav>
          </FooterColumn>

          <FooterColumn as="nav">
            <Typography $variant="h3">Contato</Typography>
            <ContactInfo>
              <p>
                <FaMapMarkerAlt aria-hidden="true" />
                <a 
                  href="https://maps.google.com/maps?q=Rua+Santana,+Vespasiano,+MG" 
                  target="_blank" 
                  rel="noopener noreferrer"
                >
                  Vespasiano, MG
                </a>
              </p>
              <p>
                <FaEnvelope aria-hidden="true" />
                <a href="mailto:contato@fagundes.tech">contato@fagundes.tech</a>
              </p>
              <p>
                <FaPhone aria-hidden="true" />
                <a 
                  href="https://wa.me/55319845552020" 
                  target="_blank" 
                  rel="noopener noreferrer"
                >
                  (31) 98455-2020
                </a>
              </p>
            </ContactInfo>
          </FooterColumn>
        </FooterGrid>

        <Copyright>
          <Typography $variant="body2" $color="secondary">
            © {new Date().getFullYear()} Fagundes Tech. Todos os direitos reservados.
          </Typography>
        </Copyright>
      </Container>
    </FooterWrapper>
  );
}; 